import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
    importWikidataEntityToKb,
    isWikidataEntityImportInFlight,
    normalizeWikidataQid,
    subscribeWikidataEntityImports,
} from '../utils/importWikidataEntityToKb';

const LOG_PREFIX = '[useWikidataAutoFetch]';
const MAX_RETRIES = 3;

/** Minimal KB list stub from NER/entities table — not a full Wikidata hydrate. */
export function isListStubSummary(entity) {
    return entity?.source === 'entities';
}

/**
 * @typedef {Object} AutoFetchOptions
 * @property {boolean} [propertiesLoading]
 * @property {number} [propertiesCount]
 * @property {() => void} [onPropertiesHydrated]
 * @property {boolean} [autoImportMissing] When true, missing KB entities hydrate immediately.
 *   Default false — KB detail requires explicit requestImport() to avoid recreating deleted entities.
 */

/**
 * Hook that triggers server-side Wikidata hydration when an entity is missing or has no properties.
 *
 * Instead of calling wikidata.org from the browser, delegates to:
 *   POST /api/entities/{entityId}/hydrate
 * via {@link importWikidataEntityToKb} (shared with Chart entity-card add paths for dedupe).
 *
 * The server fetches the full entity, resolves labels, and persists entity_summaries +
 * entity_kb_properties in one synchronous call. When it returns, both summary and properties
 * refetch automatically.
 *
 * Missing-entity import (Phase 1) is opt-in unless {@link AutoFetchOptions.autoImportMissing}
 * is true (Chart drawer). Call {@code requestImport()} from the UI to pull a candidate.
 *
 * @param {string} entityId
 * @param {Object|null} entity
 * @param {boolean} entityLoading
 * @param {Error|null} entityError
 * @param {boolean} entityNotFound
 * @param {AutoFetchOptions} [options]
 * @returns {AutoFetchState}
 */
export function useWikidataAutoFetch(
    entityId,
    entity,
    entityLoading,
    entityError,
    entityNotFound,
    options = {},
) {
    const {
        propertiesLoading = false,
        propertiesCount = 0,
        onPropertiesHydrated,
        autoImportMissing = false,
    } = options;
    const isQNumber = useMemo(() => Boolean(normalizeWikidataQid(entityId)), [entityId]);
    const [isFetching, setIsFetching] = useState(false);
    const [isFetchingProps, setIsFetchingProps] = useState(false);
    const [fetchError, setFetchError] = useState(null);
    const [fetched, setFetched] = useState(false);
    const [notFoundOnWikidata, setNotFoundOnWikidata] = useState(false);
    const [retryCount, setRetryCount] = useState(0);
    const [importRequested, setImportRequested] = useState(false);
    const [, setImportEpoch] = useState(0);

    const fetchingRef = useRef(false);
    const propsHydratingRef = useRef(false);
    const propsHydratedRef = useRef(false);
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    // Re-render when canvas-started imports begin/end so drawer can join shared in-flight state.
    useEffect(() => {
        return subscribeWikidataEntityImports(() => {
            setImportEpoch((n) => n + 1);
        });
    }, []);

    useEffect(() => {
        // Clear state that belongs to the previous entity when route param changes.
        setIsFetching(false);
        setIsFetchingProps(false);
        setFetchError(null);
        setFetched(false);
        setNotFoundOnWikidata(false);
        setRetryCount(0);
        setImportRequested(false);
        fetchingRef.current = false;
        propsHydratingRef.current = false;
        propsHydratedRef.current = false;
    }, [entityId]);

    // Phase 1: entity not found in DB → server-side hydration (opt-in unless autoImportMissing).
    useEffect(() => {
        if (entityLoading || entityError || entity !== null || !entityNotFound || fetched || notFoundOnWikidata || !isQNumber) return;
        if (!autoImportMissing && !importRequested) return;
        if (fetchingRef.current) return;

        fetchingRef.current = true;
        setIsFetching(true);
        setFetchError(null);

        console.debug(`${LOG_PREFIX} Hydration triggered (entity missing) for ${entityId} (attempt ${retryCount + 1})`);

        (async () => {
            try {
                const status = await importWikidataEntityToKb(entityId);
                if (!mountedRef.current) return;

                if (status === 'not_found') {
                    console.debug(`${LOG_PREFIX} ${entityId} not found on Wikidata (server-side)`);
                    setNotFoundOnWikidata(true);
                    return;
                }

                console.debug(`${LOG_PREFIX} Server hydration ${status} for ${entityId}`);
                setFetched(true);
                propsHydratedRef.current = true;
                onPropertiesHydrated?.();
            } catch (err) {
                if (!mountedRef.current) return;
                console.error(`${LOG_PREFIX} Server hydration error for ${entityId}:`, err);
                setFetchError(err);
            } finally {
                if (mountedRef.current) {
                    setIsFetching(false);
                    setIsFetchingProps(false);
                }
                fetchingRef.current = false;
            }
        })();
    }, [
        entityId,
        entity,
        entityLoading,
        entityError,
        entityNotFound,
        fetched,
        notFoundOnWikidata,
        isQNumber,
        retryCount,
        onPropertiesHydrated,
        autoImportMissing,
        importRequested,
    ]);

    // Properties hydration: entity exists (possibly a list stub) but properties are missing.
    useEffect(() => {
        if (!isQNumber || entityLoading || entityError || entity === null) return;
        if (propertiesLoading || propertiesCount > 0) return;
        if (propsHydratingRef.current || propsHydratedRef.current) return;
        if (isFetching) return;

        propsHydratingRef.current = true;
        setIsFetchingProps(true);
        setFetchError(null);

        console.debug(`${LOG_PREFIX} Property hydration triggered for ${entityId} (stub=${isListStubSummary(entity)})`);

        (async () => {
            try {
                const status = await importWikidataEntityToKb(entityId);
                if (!mountedRef.current) return;

                if (status === 'not_found') {
                    console.debug(`${LOG_PREFIX} ${entityId} not found on Wikidata during property hydration`);
                    setNotFoundOnWikidata(true);
                    return;
                }

                console.debug(`${LOG_PREFIX} Property hydration ${status} for ${entityId}`);
                if (isListStubSummary(entity)) {
                    setFetched(true);
                }
                propsHydratedRef.current = true;
                onPropertiesHydrated?.();
            } catch (err) {
                if (!mountedRef.current) return;
                console.error(`${LOG_PREFIX} Property hydration error for ${entityId}:`, err);
                setFetchError(err);
            } finally {
                if (mountedRef.current) {
                    setIsFetchingProps(false);
                }
                propsHydratingRef.current = false;
            }
        })();
    }, [
        entityId,
        entity,
        entityLoading,
        entityError,
        isQNumber,
        isFetching,
        propertiesLoading,
        propertiesCount,
        onPropertiesHydrated,
    ]);

    const canRetry = retryCount < MAX_RETRIES && !isFetching;

    const requestImport = useCallback(() => {
        console.debug(`${LOG_PREFIX} User requested import for ${entityId}`);
        setFetchError(null);
        setNotFoundOnWikidata(false);
        setFetched(false);
        fetchingRef.current = false;
        propsHydratedRef.current = false;
        setImportRequested(true);
    }, [entityId]);

    const retry = useCallback(() => {
        if (!canRetry) {
            console.warn(`${LOG_PREFIX} Retry blocked — max retries (${MAX_RETRIES}) reached or fetch in progress`);
            return;
        }
        console.debug(`${LOG_PREFIX} Retrying hydration for ${entityId} (retry ${retryCount + 1}/${MAX_RETRIES})`);
        setFetchError(null);
        setNotFoundOnWikidata(false);
        setFetched(false);
        fetchingRef.current = false;
        propsHydratedRef.current = false;
        setImportRequested(true);
        setRetryCount(c => c + 1);
    }, [canRetry, entityId, retryCount]);

    // Canvas may have started hydrate before the drawer opened — treat that as Importing
    // only while the summary is still missing (not during property-only refresh).
    const sharedImportInFlight = Boolean(
        isQNumber
        && entityNotFound
        && !entityLoading
        && entity === null
        && !fetchError
        && !notFoundOnWikidata
        && isWikidataEntityImportInFlight(entityId),
    );

    return {
        isFetching: isFetching || sharedImportInFlight,
        isFetchingProps,
        fetchError,
        fetched,
        notFoundOnWikidata,
        requestImport,
        retry,
        canRetry,
    };
}
